import axios from 'axios';
import type { 
  AuthResponse, 
  LoginRequest, 
  DashboardResponse, 
  UploadPreview,
  DateRange 
} from '../types';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 10000,
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API functions
export const authAPI = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
  },
  
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('access_token');
  },
};

// Dashboard API functions
export const dashboardAPI = {
  getDashboardData: async (params?: {
    date_from?: string;
    date_to?: string;
    date_type?: 'delivery' | 'pickup';
    excluded_drivers?: string[];
    equipment_types?: string[];
    rpm_exclusion_enabled?: boolean;
    rpm_exclusion_min?: number;
    rpm_exclusion_max?: number;
    rpm_min_enabled?: boolean;
    rpm_max_enabled?: boolean;
  }): Promise<DashboardResponse> => {
    const response = await api.get('/dashboard', { 
      params,
      paramsSerializer: {
        indexes: null // This makes arrays serialize as ?excluded_drivers=A&excluded_drivers=B&equipment_types=X&equipment_types=Y
      }
    });
    return response.data;
  },
  
  getAvailableDrivers: async (): Promise<{ drivers: string[] }> => {
    const response = await api.get('/drivers');
    return response.data;
  },

  getAvailableEquipmentTypes: async (): Promise<{ equipment_types: string[] }> => {
    const response = await api.get('/equipment-types');
    return response.data;
  },
};

// CSV Upload API functions
export const uploadAPI = {
  previewCSV: async (file: File): Promise<UploadPreview> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/upload/preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  processCSV: async (file: File, mapping: Record<string, string>) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    
    const response = await api.post('/upload/process', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Health check
export const healthAPI = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

export default api;